import {
  ChevronDown,
  Clock,
  Download,
  ExternalLink,
  FileImage,
  FileText,
  HelpCircle,
  Loader2,
  Mail,
  Share2,
  TableProperties,
  MessageSquare
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface ExportToolbarProps {
  isExporting: boolean;
  isExpanded: boolean;
  onExportImage: () => void;
  onExportPDF: () => void;
  onExportCSV: () => void;
  onSendEmail: () => void;
  onShareCalendar: () => void;
  onHelpClick: () => void;
  onScheduleChatClick?: () => void; // Optional - only shown when scheduling is enabled
  showScheduleButton?: boolean; // Control visibility
}

export function ExportToolbar({
                                isExpanded,
                                isExporting,
                                onExportImage,
                                onExportPDF,
                                onExportCSV,
                                onSendEmail,
                                onShareCalendar,
                                onHelpClick,
                                onScheduleChatClick,
                                showScheduleButton = true,
                              }: ExportToolbarProps) {

  return (
    <div className="flex items-center justify-between gap-2 sm:gap-4">
      <div className="flex items-center gap-2 sm:gap-3 flex-shrink min-w-0">
        <a
          href="https://www.kontentino.com"
          target="_blank"
          rel="noopener noreferrer"
          className="hidden sm:inline-flex items-center gap-1.5 text-sm text-primary hover:text-primary/80 font-medium transition-colors group whitespace-nowrap"
        >
          <span>{isExpanded && 'Automate your workflow at '}Kontentino.com</span>
          <ExternalLink className="h-3.5 w-3.5 opacity-60 group-hover:opacity-100 transition-opacity"/>
        </a>
        <button
          onClick={onHelpClick}
          className="p-2 text-muted-foreground hover:text-foreground hover:bg-muted rounded-md transition-colors flex-shrink-0"
          title="Calendar tips and help"
        >
          <HelpCircle className="h-4 w-4"/>
        </button>
      </div>

      <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              disabled={isExporting}
              className="whitespace-nowrap"
              title="Download calendar"
            >
              {isExporting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin"/>
                  {isExpanded && <span className="hidden sm:inline ml-2">Exporting...</span>}
                </>
              ) : (
                <>
                  <Download className="h-4 w-4"/>
                  {isExpanded && <span className="hidden sm:inline ml-2">Download</span>}
                  <ChevronDown className="h-4 w-4 ml-1"/>
                </>
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={onExportPDF}>
              <FileText className="h-4 w-4 mr-2"/>
              Export as PDF
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onExportImage}>
              <FileImage className="h-4 w-4 mr-2"/>
              Export as PNG
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onExportCSV}>
              <TableProperties className="h-4 w-4 mr-2"/>
              Export as CSV
            </DropdownMenuItem>
            <DropdownMenuSeparator/>
            <DropdownMenuItem onClick={onShareCalendar}>
              <Share2 className="h-4 w-4 mr-2"/>
              Share Calendar Link
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          variant="default"
          size="sm"
          onClick={onSendEmail}
          disabled={isExporting}
          className="whitespace-nowrap"
          title="Send for approval"
        >
          <Mail className="h-4 w-4 sm:mr-2"/>
          <span className="hidden sm:inline">Send for Approval</span>
        </Button>

        {showScheduleButton && onScheduleChatClick && (
          <Button
            variant="success"
            size="sm"
            onClick={onScheduleChatClick}
            disabled={isExporting}
            className="whitespace-nowrap"
            title="Schedule posts"
          >
            <Clock className="h-4 w-4 sm:mr-2"/>
            <span className="hidden sm:inline">{isExpanded ? 'Schedule Posts' : 'Schedule'}</span>
          </Button>
        )}
      </div>
    </div>
  );
}
